import React from 'react';
import { cn } from '../../lib/cn';
import { DaySummary } from '../../lib/calc';
import { fmtCount } from '../../lib/format';
import { useDisplayMode } from '../../context/DisplayModeContext';
export const CELL_STYLE: React.CSSProperties = {
  position: 'relative',
  height: 'clamp(105px, 12vh, 132px)',
  minHeight: 105,
  borderRadius: 6,
  borderWidth: 1,
  borderStyle: 'solid',
  padding: '8px 9px'
};
export function DayCell({
  day,
  inMonth,
  isToday,
  summary,
  reviewPending,
  selected,
  onClick
}: {
  day: number;
  inMonth: boolean;
  isToday: boolean;
  summary?: DaySummary;
  reviewPending?: boolean;
  selected: boolean;
  onClick: () => void;
}) {
  const {
    formatResult
  } = useDisplayMode();
  const hasOps = !!summary && summary.count > 0;
  const pos = hasOps && summary!.resultPct >= 0;
  const palette = !inMonth ? {
    background: '#e7e7e9',
    borderColor: '#e1e2e5',
    meta: '#a9abb0',
    result: '#a9abb0'
  } : hasOps ? pos ? {
    background: '#eef9f1',
    borderColor: '#65cf98',
    meta: '#525866',
    result: '#16b866'
  } : {
    background: '#fff2f0',
    borderColor: '#ef9188',
    meta: '#525866',
    result: '#ef5b50'
  } : {
    background: '#ffffff',
    borderColor: '#eceef2',
    meta: '#525866',
    result: '#525866'
  };
  const showPlan = hasOps && inMonth && summary!.planUnknown === 0;
  const showReview = !!reviewPending && inMonth;
  return <button type="button" onClick={onClick} style={{
    ...CELL_STYLE,
    background: palette.background,
    borderColor: palette.borderColor
  }} className={cn('block w-full min-w-0 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40', selected && 'ring-2 ring-accent/55')} data-id="element-42" data-mp="ssrrvweptj3j">
      {hasOps && <span style={{
      position: 'absolute',
      top: 8,
      left: 9,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: 1.2,
      color: palette.meta
    }} className="max-w-[75%] truncate" data-id="element-43" data-mp="a2tincz6ljyb">
          {fmtCount(summary!.count)}
        </span>}

      <span style={{
      position: 'absolute',
      top: 8,
      right: 9,
      fontSize: 12,
      fontWeight: isToday && inMonth ? 700 : 500,
      lineHeight: 1.2,
      color: palette.meta
    }} className="tabular-nums" data-id="element-44" data-mp="1higsggnr4up0">
        {day}
      </span>

      {hasOps && <span style={{
      position: 'absolute',
      left: 9,
      bottom: 9,
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 1,
      color: palette.result
    }} className="tabular-nums" data-id="element-45" data-mp="1w6blxz9l3335">
          {formatResult({
        pct: summary!.resultPct,
        money: summary!.resultMoney
      })}
        </span>}

      {(showPlan || showReview) && <span style={{
      position: 'absolute',
      right: 9,
      bottom: 9
    }} className="flex items-center gap-1" data-id="element-46" data-mp="18jmvuqaf84bg">
          {showReview && <ReviewBadge data-id="element-47" data-mp="2jwyveoz5e0rt" />}
          {showPlan && <PlanBadge summary={summary!} data-id="element-48" data-mp="2jvw5okdka4dh" />}
        </span>}
    </button>;
}

/**
 * Plan compliance indicator. Shown only once every operation of the day has an
 * answer: all followed -> check, any broken -> cross.
 */
function PlanBadge({
  summary
}: {
  summary: DaySummary;
}) {
  const broken = summary.planBroken > 0;
  const title = summary.count > 1 ? `${summary.planFollowed} de ${summary.count} operaciones siguieron el plan` : broken ? 'Plan incumplido' : 'Plan seguido';
  return <span style={{
    width: 20,
    height: 20,
    fontSize: 12,
    lineHeight: 1,
    borderRadius: '50%',
    borderWidth: 1,
    borderStyle: 'solid',
    background: broken ? '#fff0ee' : '#eaf8ef',
    color: broken ? '#ef5d52' : '#18b866',
    borderColor: broken ? '#f2b7b1' : '#b8e8c9'
  }} className="flex items-center justify-center font-medium" title={title} aria-label={title} data-id="element-49" data-mp="3uo5a4bslfocx">
      {broken ? '\u00D7' : '\u2713'}
    </span>;
}

/** Small amber dot: this day still needs review. Cell colour stays untouched. */
function ReviewBadge() {
  return <span style={{
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: '#fff4bf',
    border: '1px solid #e2bd42',
    color: '#8a6a00',
    fontSize: 10,
    lineHeight: 1
  }} className="flex items-center justify-center font-semibold" title="Pendiente de revisión" aria-label="Pendiente de revisión" data-id="element-50" data-mp="6nijzzm2u0oh">
      !
    </span>;
}